import { Injectable } from '@angular/core';
import { shortMonths, shortWeekdays } from '@app/core/services/utilities.service';
import { UserState } from '@app/core/store/states/user/user.state';
import { GetDateWithoutTimeZone, getPrevMonthToDate, LocalizedDatePattern } from '@app/core/utilities/date.utilities';
import { StringifyArray } from '@app/core/utilities/string.utilities';
import { Store } from '@ngxs/store';
import add from 'date-fns/add';
import addDays from 'date-fns/addDays';
import endOfDay from 'date-fns/endOfDay';
import endOfMonth from 'date-fns/endOfMonth';
import format from 'date-fns/format';
import getDate from 'date-fns/getDate';
import getDay from 'date-fns/getDay';
import getDaysInMonth from 'date-fns/getDaysInMonth';
import getMonth from 'date-fns/getMonth';
import getWeek from 'date-fns/getWeek';
import getYear from 'date-fns/getYear';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isEqual from 'date-fns/isEqual';
import isWeekend from 'date-fns/isWeekend';
import setHours from 'date-fns/setHours';
import setMinutes from 'date-fns/setMinutes';
import setSeconds from 'date-fns/setSeconds';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import sub from 'date-fns/sub';
import {
  Calendar,
  CalendarOptions,
  CalendarState,
  DateRangeOption,
  DateRangesTypes,
  Dropdown,
  Locale,
  SideEnum,
} from './date-range.models';
import { GetPreviousDateRange } from './date-range.utilities';

@Injectable()
export class DateRangeService {
  options: CalendarOptions = { linkedCalendars: false, dateLimit: null };
  calendarState = { left: <CalendarState>{}, right: <CalendarState>{} };
  today = startOfDay(new Date());
  tomorrow = addDays(this.today, 1);
  locale: Locale;
  ranges: DateRangeOption[] = [
    {
      type: DateRangesTypes.YDY,
      heapName: 'yesterday',
      active: false,
      value: [addDays(this.today, -1), addDays(this.today, -1)],
    },
    {
      type: DateRangesTypes.LWK,
      heapName: 'lastWeek',
      active: false,
      value: [addDays(startOfWeek(addDays(this.today, -1)), -6), startOfWeek(addDays(this.today, -1))],
    },
    {
      type: DateRangesTypes.LMN,
      heapName: 'lastMonth',
      active: false,
      value: [
        startOfMonth(addDays(startOfMonth(this.today), -1)),
        GetDateWithoutTimeZone(endOfMonth(addDays(startOfMonth(this.today), -1))),
      ],
    },
    {
      type: DateRangesTypes.L7,
      heapName: 'last7Days',
      active: false,
      value: [addDays(this.today, -7), addDays(this.today, -1)],
    },
    {
      type: DateRangesTypes.L30,
      heapName: 'last30Days',
      active: false,
      value: [addDays(this.today, -30), addDays(this.today, -1)],
    },
    {
      type: DateRangesTypes.TMN,
      heapName: 'thisMonth',
      active: false,
      value: [startOfMonth(this.today), addDays(this.today, -1)],
    },
  ];
  startDateRangeOption: DateRangeOption = {
    type: DateRangesTypes.ALL,
    heapName: 'AllTimeWithAdspert',
    active: false,
    value: null,
  };
  customRange: DateRangeOption = { type: DateRangesTypes.CUSTOM, heapName: 'custom', active: false, value: null };

  private _shiftFirstDayOfWeek() {
    const daysOfWeek = [...this.locale.daysOfWeek];
    this.locale.firstDay = this.locale.firstDay % 7;
    if (this.locale.firstDay !== 0) {
      let iterator = this.locale.firstDay;
      while (iterator > 0) {
        daysOfWeek.push(daysOfWeek.shift());
        iterator--;
      }
    }
    this.locale.daysOfWeek = daysOfWeek;
  }

  getPreviousPeriodDates(dates: [Date, Date], activeType?: DateRangesTypes): [Date, Date] | null {
    if (activeType === DateRangesTypes.LMN) {
      const tmpPrevMonth = sub(dates[0], { months: 1 });
      return [startOfMonth(tmpPrevMonth), endOfMonth(tmpPrevMonth)];
    } else if (activeType === DateRangesTypes.TMN) {
      return getPrevMonthToDate(dates);
    } else {
      return GetPreviousDateRange(dates);
    }
  }

  getCalendar(
    side: SideEnum,
    dates: Date[],
    currentSelectedMonth: Date,
    minDateLimit: Date,
    maxDateLimit: Date,
  ): Calendar {
    const month = getMonth(currentSelectedMonth);
    const year = getYear(currentSelectedMonth);
    const hour = 0;
    const minute = 0;
    const second = 0;
    const daysInMonth = getDaysInMonth(new Date(year, month));
    const firstDay = startOfMonth(new Date(year, month));
    const lastDay = endOfMonth(new Date(year, month));
    const lastMonth = getMonth(sub(firstDay, { months: 1 }));
    const lastYear = getYear(sub(firstDay, { months: 1 }));
    const daysInLastMonth = getDaysInMonth(new Date(lastYear, lastMonth));
    const dayOfWeek = getDay(firstDay);
    // initialize a 6 rows x 7 columns array for the calendar
    const calendar: Calendar = {
      grid: [],
      firstDay: firstDay,
      lastDay: lastDay,
    };
    for (let i = 0; i < 6; i++) {
      calendar.grid[i] = [];
    }

    // populate the calendar with date objects
    let startDay = daysInLastMonth - dayOfWeek + this.locale.firstDay + 1;
    if (startDay > daysInLastMonth) startDay -= 7;

    if (dayOfWeek === this.locale.firstDay) startDay = daysInLastMonth - 6;

    let curDate = new Date(lastYear, lastMonth, startDay, 12, minute, second);

    for (let i = 0, col = 0, row = 0; i < 42; i++, col++, curDate = add(curDate, { hours: 24 })) {
      if (i > 0 && col % 7 === 0) {
        col = 0;
        row++;
      }
      calendar.grid[row][col] = setSeconds(setMinutes(setHours(curDate, hour), minute), second);
      curDate = setHours(curDate, 12);
      if (
        minDateLimit &&
        format(calendar.grid[row][col], 'yyyy-MM-dd') === format(minDateLimit, 'yyyy-MM-dd') &&
        isBefore(calendar.grid[row][col], minDateLimit) &&
        side === SideEnum.left
      ) {
        calendar.grid[row][col] = minDateLimit;
      }

      if (
        maxDateLimit &&
        format(calendar.grid[row][col], 'yyyy-MM-dd') === format(maxDateLimit, 'yyyy-MM-dd') &&
        isAfter(calendar.grid[row][col], maxDateLimit) &&
        side === SideEnum.right
      ) {
        calendar.grid[row][col] = maxDateLimit;
      }
    }

    //
    // Display the calendar
    //
    const minDate = side === SideEnum.left ? minDateLimit : dates[0];
    let maxDate = maxDateLimit;
    // adjust maxDate to reflect the calendar.options.dateLimit setting in order to
    // grey out end dates beyond the calendar.options.dateLimit
    if (dates[1] === null && this.options.dateLimit) {
      const maxLimit = endOfDay(add(dates[0], { days: this.options.dateLimit }));
      if (!maxDate || isBefore(maxLimit, maxDate)) maxDate = maxLimit;
    }

    this.calendarState[side] = {
      month,
      year,
      hour,
      minute,
      second,
      daysInMonth,
      firstDay,
      lastDay,
      lastMonth,
      lastYear,
      daysInLastMonth,
      dayOfWeek,
      calRows: Array.from(Array(6).keys()),
      calCols: Array.from(Array(7).keys()),
      classes: {},
      minDate,
      maxDate,
      calendar,
      dropdowns: {} as Dropdown,
    };
    const currentMonth = getMonth(calendar.grid[1][1]);
    const currentYear = getYear(calendar.grid[1][1]);
    const realCurrentYear = getYear(this.today);
    const maxYear = (maxDate && getYear(maxDate)) || realCurrentYear + 5;
    const minYear = (minDate && getYear(minDate)) || realCurrentYear - 50;
    const inMinYear = currentYear === minYear;
    const inMaxYear = currentYear === maxYear;
    const yearArrays = [];

    for (let y = minYear; y <= maxYear; y++) {
      yearArrays.push(y);
    }

    this.calendarState[side].dropdowns = {
      currentMonth,
      currentYear,
      maxYear,
      minYear,
      inMinYear,
      inMaxYear,
      yearArrays: yearArrays,
      monthArrays: Array.from(Array(12).keys()),
    };

    return calendar;
  }

  buildCells(
    side: SideEnum,
    grid,
    pickingDate: boolean,
    pickingCompareDate: boolean,
    hasCompareRange: boolean,
    enableCompareSelection: boolean,
    dates: Date[],
    compareDates: Date[],
    minDate: Date,
    nowHoveredDate: Date,
  ) {
    for (let row = 0; row < 6; row++) {
      this.calendarState[side].classes[row] = {};
      const rowClasses = [];
      for (let col = 0; col < 7; col++) {
        try {
          const classes = [];
          // highlight today's date
          if (isEqual(grid[row][col], this.today)) {
            classes.push('today');
          }
          // highlight weekends
          if (isWeekend(grid[row][col])) {
            classes.push('weekend');
          }
          // grey out the dates in other months displayed at beginning and end of this grid
          if (getMonth(grid[row][col]) !== getMonth(grid[1][1])) {
            classes.push('off');
          }
          // don't allow selection of dates before the minimum date
          if (minDate && isBefore(grid[row][col], minDate)) {
            classes.push('off', 'disabled');
          }
          // don't allow selection of dates after the maximum date
          if (this.calendarState[side].maxDate && isAfter(grid[row][col], this.calendarState[side].maxDate)) {
            classes.push('disabled'); // 'off',
          }
          if (dates[1] && isAfter(grid[row][col], dates[1])) {
            classes.push('after-max-date');
          }
          // don't allow selection of date if a custom function decides it's invalid
          // enable this when isInvalidDate is used
          // if (this.isInvalidDate(calendar[row][col])) {
          //   classes.push('off', 'disabled', 'invalid');
          // }

          // disable dates when compare selection is turned on
          if (enableCompareSelection && (isAfter(grid[row][col], dates[0]) || isEqual(grid[row][col], dates[0]))) {
            classes.push('disable-selected-dates');
          }
          // highlight the currently selected start date
          if (dates[0] && format(grid[row][col], 'yyyy-MM-dd') === format(dates[0], 'yyyy-MM-dd')) {
            classes.push('active', 'start-date');
            if (!dates[1]) {
              classes.push('selection-incomplete');
            }
          }
          // highlight the currently selected end date
          if (dates[1] != null && format(grid[row][col], 'yyyy-MM-dd') === format(dates[1], 'yyyy-MM-dd')) {
            classes.push('active', 'end-date');
          }
          // highlight dates in-between the selected dates
          if (
            ((nowHoveredDate != null && pickingDate) || dates[1] != null) &&
            +grid[row][col] > +dates[0] &&
            (+grid[row][col] < +dates[1] || (+grid[row][col] < +nowHoveredDate && pickingDate)) &&
            !classes.find((el) => el === 'off')
          ) {
            classes.push('in-range');
            if (!dates[1]) {
              classes.push('selection-incomplete');
            }
          }
          if (hasCompareRange) {
            // highlight the currently selected start date
            if (compareDates[0] && format(grid[row][col], 'yyyy-MM-dd') === format(compareDates[0], 'yyyy-MM-dd')) {
              classes.push('active', 'compare-start-date');
              if (!compareDates[1]) {
                classes.push('selection-incomplete');
              }
            }
            // highlight the currently selected end date
            if (
              compareDates[1] != null &&
              format(grid[row][col], 'yyyy-MM-dd') === format(compareDates[1], 'yyyy-MM-dd')
            ) {
              classes.push('active', 'compare-end-date');
            }
            // highlight dates in-between the selected dates
            if (
              ((nowHoveredDate != null && pickingCompareDate) || compareDates[1] != null) &&
              +grid[row][col] > +compareDates[0] &&
              (+grid[row][col] < +compareDates[1] || (+grid[row][col] < +nowHoveredDate && pickingCompareDate)) &&
              !classes.find((el) => el === 'off')
            ) {
              classes.push('compare-in-range');
              if (!compareDates[1]) {
                classes.push('selection-incomplete');
              }
            }
          }

          // apply custom classes for this date
          // enable this when isCustomDate is used
          // const isCustom = this.isCustomDate(grid[row][col]);
          // if (isCustom !== false) {
          //   if (typeof isCustom === 'string') {
          //     classes.push(isCustom);
          //   } else {
          //     Array.prototype.push.apply(classes, isCustom);
          //   }
          // }

          // store classes var
          let cname = '';
          let disabled = false;
          for (let i = 0; i < classes.length; i++) {
            cname += classes[i] + ' ';
            if (classes[i] === 'disabled') {
              disabled = true;
            }
          }
          if (!disabled) {
            cname += 'available';
          }
          this.calendarState[side].classes[row][col] = cname.replace(/^\s+|\s+$/g, '');
        } catch (error) {
          throw new Error(StringifyArray('invalid time error', compareDates, grid[row][col], row, col));
        }
      }
      this.calendarState[side].classes[row].classList = rowClasses.join(' ');
    }
  }

  getMinDateMonth(isLeft) {
    try {
      return getMonth(this.calendarState[isLeft ? SideEnum.left : SideEnum.right].minDate);
    } catch (error) {
      return '';
    }
  }
  getMaxDateMonth(isLeft) {
    try {
      return getMonth(this.calendarState[isLeft ? SideEnum.left : SideEnum.right].maxDate);
    } catch (error) {
      return '';
    }
  }
  getDisplayDay(row, col, isLeft) {
    try {
      return getDate(this.calendarState[isLeft ? SideEnum.left : SideEnum.right].calendar.grid[row][col]);
    } catch (error) {
      return '';
    }
  }
  getDisplayDate(row, col, isLeft) {
    try {
      return +this.calendarState[isLeft ? SideEnum.left : SideEnum.right].calendar.grid[row][col];
    } catch (error) {
      return '';
    }
  }

  getDisplayWeek(row, isLeft) {
    try {
      return getWeek(this.calendarState[isLeft ? SideEnum.left : SideEnum.right].calendar.grid[row][0]);
    } catch (error) {
      return '';
    }
  }
  showSelectedMonth(isLeft) {
    try {
      return this.locale.monthNames[
        getMonth(this.calendarState[isLeft ? SideEnum.left : SideEnum.right].calendar.grid[1][1])
      ];
    } catch (error) {}
    return '';
  }
  showSelectedYear(isLeft) {
    try {
      return format(this.calendarState[isLeft ? SideEnum.left : SideEnum.right].calendar.grid[1][1], ' yyyy');
    } catch (error) {}
    return '';
  }
  isBeforeMinDate(isLeft) {
    try {
      return isBefore(
        this.calendarState[isLeft ? SideEnum.left : SideEnum.right].minDate,
        this.calendarState[isLeft ? SideEnum.left : SideEnum.right].calendar.firstDay,
      );
    } catch (error) {
      return '';
    }
  }

  getHoveredDate(side: SideEnum, row: number, col: number) {
    const leftCalDate = this.calendarState.left.calendar.grid[row][col];
    const rightCalDate = this.calendarState.right.calendar.grid[row][col];
    return side === SideEnum.left ? leftCalDate : rightCalDate;
  }
  constructor(private store: Store) {
    const user = this.store.selectSnapshot(UserState.user);
    this.locale = {
      daysOfWeek: shortWeekdays[user.lang],
      monthNames: shortMonths[user.lang],
      format: 'dd/MM/yyyy',
      displayFormat: LocalizedDatePattern('dd MMM, yyyy'),
      firstDay: 1,
    };
    this._shiftFirstDayOfWeek();
  }
}
